import { NavigationProp, useNavigation } from "@react-navigation/native";
import { useMutation } from "@tanstack/react-query";
import { Divider, Text } from "@ui-kitten/components";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import Button from "components/Button";
import Dialog from "components/Dialog";
import Spacer from "components/Spacer";
import dayjs from "dayjs";
import { SubscriptionDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import useUpgradablePlansForSubscription from "hooks/useUpgradablePlansForSubscription";
import { AccountStackParamList } from "navigation/types";
import { cancelOrReinstateSubscriptionFetcher } from "queries/account.queries";
import React, { useState } from "react";
import { StyleSheet, View } from "react-native";
import PlanNameWithPaymentInterval from "screens/Account/components/PlanNameWithPaymentInterval";
import { toAddNewPlan, toUpgradePlan } from "utils/navigationUtils";
import { getPlanType } from "utils/planPackageUtils";
import toast from "utils/toast";
import { getUserName } from "utils/userUtils";

interface MemberSubscriptionListProps {
  memberId: number;
  currentMemberSubscriptions: SubscriptionDetails[];
}

interface RenderPlanDetailProps {
  accountSubscriptions: SubscriptionDetails[];
  subscription: SubscriptionDetails;
  onPlandetailsPress: (sub: SubscriptionDetails) => void;
  onCancelRenewalPress: (sub: SubscriptionDetails) => void;
}

const RenderPlanDetail: React.FC<RenderPlanDetailProps> = ({
  accountSubscriptions,
  subscription,
  onPlandetailsPress,
  onCancelRenewalPress,
}) => {
  const {
    planPackage,
    billingInterval,
    monthlyPrice,
    member: { id },
  } = subscription;
  const upgradablePlansForSelectedSubscription =
    useUpgradablePlansForSubscription(subscription);
  const isNotVoucherPlan = getPlanType(planPackage) !== "VOUCHER";

  const navigation = useNavigation<NavigationProp<AccountStackParamList>>();

  // returns true if account has some subscription that's canceled and not expired
  const accountHasCancelledSubscription = accountSubscriptions.some(
    (sub) => sub.status === "CANCELLED" && sub.expired === false
  );

  return (
    <View>
      {subscription.status === "CANCELLED" && (
        <Text status="danger">
          Plan expiration date:{" "}
          {dayjs(subscription.expiresAt).format("ddd MMM DD YYYY")}
        </Text>
      )}
      <PlanNameWithPaymentInterval
        planPackage={planPackage}
        billingInterval={billingInterval}
        monthlyPrice={monthlyPrice}
      />
      <BaseCardListItem
        title="Plan details"
        onPress={() => onPlandetailsPress(subscription)}
      />

      {planPackage.billingIntervals.length > 1 && // if plan package supports pay in full
        billingInterval === "MONTHLY" && // if current subscription is paid monthly
        subscription.status === "ACTIVE" && // if subscription is active
        accountHasCancelledSubscription === false && (
          <BaseCardListItem
            testID="change-payment-interval-button"
            title="Change payment interval"
            onPress={() =>
              navigation.navigate("UpdateBillingInterval", {
                pid: String(planPackage.id),
                mid: String(id),
              })
            }
          />
        )}

      {planPackage.cancelDisabled === false && ( // only if subscription is paid monthly and cancelDisabled flag is false, we allow user to cancel renewal
        <BaseCardListItem
          testID="cancelOrReinstate-button"
          title={
            subscription.status === "ACTIVE"
              ? "Cancel renewal"
              : "Reinstate renewal"
          }
          onPress={() => onCancelRenewalPress(subscription)}
        />
      )}

      {upgradablePlansForSelectedSubscription.length > 0 &&
        isNotVoucherPlan &&
        subscription.status === "ACTIVE" && (
          <>
            <Divider style={{ marginVertical: 15 }} />
            <Button
              onPress={() => {
                toUpgradePlan(String(subscription.id));
              }}
              testID="upgrade-plan-button"
            >
              Upgrade Available
            </Button>
          </>
        )}
    </View>
  );
};

const MemberSubscriptionList: React.FC<MemberSubscriptionListProps> = ({
  currentMemberSubscriptions,
  memberId,
}) => {
  const [planDetailDialog, setPlanDetailDialog] = useState(false);
  const [cancelRenewalDialog, setCancelRenewalDialog] = useState(false);
  const [currentSelectedSub, setCurrentSelectedSub] =
    useState<SubscriptionDetails | null>(null);

  const { account, fetchAccount } = useAccount();
  const { mutateAsync: cancelOrReinstate, isLoading: loading } = useMutation(
    cancelOrReinstateSubscriptionFetcher
  );

  const handlePlandetailsPress = (sub: SubscriptionDetails) => {
    setCurrentSelectedSub(sub);
    setPlanDetailDialog(true);
  };

  const handleCancelRenewalPress = (sub: SubscriptionDetails) => {
    setCurrentSelectedSub(sub);
    setCancelRenewalDialog(true);
  };

  const isCurrentSubActive = currentSelectedSub?.status === "ACTIVE" || false;

  const handleCancelOrReinstateSubscription = async () => {
    if (!currentSelectedSub || !account) return;
    await cancelOrReinstate({
      accountId: account!.id,
      subscriptionId: currentSelectedSub.id,
      type: isCurrentSubActive ? "cancel" : "reinstate",
    });
    fetchAccount();
    setCancelRenewalDialog(false);
    toast.success(
      `You have ${isCurrentSubActive ? "cancelled" : "reinstated"} ${
        currentSelectedSub.planPackage.name
      } for ${getUserName(currentSelectedSub.member)}.`
    );
  };

  const getReservationWindows = (subscription: SubscriptionDetails) => {
    const reservationWindowHours =
      subscription.planPackage.primaryComponent.reservationWindowHours;
    if (reservationWindowHours < 0) {
      return "Reserve any time";
    }
    const days = reservationWindowHours / 24;
    return `${days}-day`;
  };

  const getMaxVouchersPerGame = (subscription: SubscriptionDetails) => {
    const perEventMax = subscription.planPackage.primaryComponent.perEventMax;
    return perEventMax < 0 ? "Unlimited" : perEventMax;
  };

  return (
    <View>
      {currentMemberSubscriptions.map((sub) => (
        <View key={sub.id}>
          <BaseCard>
            <RenderPlanDetail
              accountSubscriptions={account?.subscriptions || []}
              subscription={sub}
              onPlandetailsPress={handlePlandetailsPress}
              onCancelRenewalPress={handleCancelRenewalPress}
            ></RenderPlanDetail>
          </BaseCard>
          <Spacer />
        </View>
      ))}

      <BaseCard title="Add Plan to This Member">
        <BaseCardListItem
          title="Add new plan"
          onPress={() => toAddNewPlan(String(memberId))}
        />
      </BaseCard>

      <Spacer />

      {currentSelectedSub !== null && (
        <>
          {/* plan detail dialog */}
          <Dialog
            visible={planDetailDialog}
            setVisible={setPlanDetailDialog}
            cancelText="Cancel"
          >
            <BaseCard title={`${currentSelectedSub.planPackage.name} details`}>
              <BaseCardListItem
                title={`Reservation window: ${getReservationWindows(
                  currentSelectedSub
                )}`}
                showArrow={false}
              />
              <BaseCardListItem
                title={`Status: ${currentSelectedSub.status}`}
                showArrow={false}
              />

              {getPlanType(currentSelectedSub.planPackage) === "VOUCHER" && (
                <BaseCardListItem
                  title={`Max vouchers per game: ${getMaxVouchersPerGame(
                    currentSelectedSub
                  )}`}
                  showArrow={false}
                />
              )}
            </BaseCard>
          </Dialog>
          {/* cancel/reinstate subscription dialog */}
          <Dialog
            visible={cancelRenewalDialog}
            setVisible={setCancelRenewalDialog}
            cancelText="Cancel"
            okText="Confirm"
            onOk={handleCancelOrReinstateSubscription}
            okBtnProps={{ loading, testID: "cancelOrReinstate-confirm-button" }}
          >
            <Text category="h6">
              {isCurrentSubActive ? "Cancel" : "Reinstate"} Membership
            </Text>

            <Spacer margin={5} />
            <Text>
              You are {isCurrentSubActive ? "cancelling" : "reinstating"}{" "}
              renewal for:
            </Text>
            <Text style={styles.withMarginAndBold}>
              {currentSelectedSub.planPackage.name}
            </Text>
            <Text>for member</Text>
            <Text style={styles.withMarginAndBold}>
              {getUserName(currentSelectedSub.member)}
            </Text>
            {isCurrentSubActive && (
              <Text>
                your membership will remain active until the next renewal
                period.
              </Text>
            )}
          </Dialog>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  withMarginAndBold: { marginVertical: 5, fontWeight: "700" },
});
export default MemberSubscriptionList;
